import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Zap, Trophy, EyeOff, Brain } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { BeltBar } from "@/components/BeltBadge";
import { Avatar } from "@/components/Avatar";
import { LevelBar } from "@/components/XpChip";

const KINDS = [
  { v: "all", l: "All-time" },
  { v: "week", l: "This week" },
];

function Row({ e, me }) {
  const podium = e.rank <= 3;
  return (
    <div data-testid={`leaderboard-row-${e.rank}`} className={`flex items-center gap-4 px-4 py-3.5 ${me ? "bg-[#E50914]/10" : ""}`}>
      <span className={`w-6 text-center font-display text-lg font-semibold ${podium ? "text-[#E50914]" : "text-[#71717A]"}`}>{e.rank}</span>
      <Avatar id={e.id} version={e.avatar_version} name={e.display_name} className="w-10 h-10 rounded-full" textClass="text-base" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{e.display_name}{me && <span className="text-xs text-[#E50914] ml-2">You</span>}</div>
        <div className="flex items-center gap-2 mt-1">
          <BeltBar belt={e.belt} stripes={e.stripes} className="w-10 h-2" />
          <span className="text-[11px] text-[#71717A] truncate">Level {e.level}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Zap size={14} className="text-[#E50914] fill-[#E50914]" />
        <span className="font-display text-xl font-semibold tabular-nums">{e.value}</span>
      </div>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [kind, setKind] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["leaderboard", kind], queryFn: () => api.get(`/leaderboard?kind=${kind}`).then((r) => r.data) });

  return (
    <div>
      <PageHeader eyebrow="Earn XP in quizzes" title="Ranks" />
      <div className="px-5 space-y-4">
        <div className="card p-5 rise" data-testid="ranks-level-card">
          <LevelBar user={user} />
          <Link to="/quizzes" data-testid="ranks-earn-xp" className="mt-4 flex items-center justify-between gap-3 p-3 rounded-xl bg-[#111113] border border-[#27272A] hover:border-[#E50914] transition-colors">
            <span className="flex items-center gap-2.5 text-sm"><Brain size={16} className="text-[#E50914]" /> Beginner 10 · Intermediate 20 · Advanced 30 XP</span>
            <span className="text-xs font-semibold text-[#A1A1AA]">Quiz</span>
          </Link>
          <p className="text-[11px] text-[#71717A] mt-2.5">XP per correct answer, first time only. Personal quizzes don't award XP.</p>
        </div>

        <div className="grid grid-cols-2 gap-1 p-1 bg-[#161618] border border-[#27272A] rounded-xl" data-testid="leaderboard-tabs">
          {KINDS.map((k) => (
            <button key={k.v} data-testid={`leaderboard-tab-${k.v}`} onClick={() => setKind(k.v)} className={`h-10 rounded-lg text-sm font-semibold transition-colors duration-200 ${kind === k.v ? "bg-[#E50914] text-white" : "text-[#A1A1AA] hover:text-white"}`}>
              {k.l}
            </button>
          ))}
        </div>

        {data?.hidden && (
          <div className="card p-4 flex items-center gap-3 text-sm" data-testid="leaderboard-hidden-note">
            <EyeOff size={18} className="text-[#71717A] shrink-0" />
            <span className="text-[#A1A1AA]">You're hidden from the leaderboard. <Link to="/profile" className="text-white underline decoration-[#E50914] underline-offset-4">Change</Link></span>
          </div>
        )}

        {data && !data.hidden && (
          <div className="card p-4 flex items-center justify-between" data-testid="leaderboard-my-rank">
            <div>
              <div className="eyebrow mb-1">Your rank</div>
              <div className="font-display text-2xl font-semibold">{data.me ? `#${data.me.rank}` : "—"}</div>
            </div>
            <div className="text-right">
              <div className="eyebrow mb-1">{kind === "week" ? "This week" : "Total"}</div>
              <div className="font-display text-2xl font-semibold" data-testid="leaderboard-my-xp">{kind === "week" ? data.my_week_xp : data.my_xp} <span className="text-sm text-[#71717A]">XP</span></div>
            </div>
          </div>
        )}

        {isLoading && <div className="card h-64 animate-pulse" />}
        {data && data.entries.length > 0 && (
          <div className="card divide-y divide-[#1F1F23] overflow-hidden rise" data-testid="leaderboard-list">
            {data.entries.map((e) => <Row key={e.id} e={e} me={e.id === user.id} />)}
          </div>
        )}
        {data && data.entries.length === 0 && (
          <div className="card p-8 text-center" data-testid="leaderboard-empty">
            <Trophy className="mx-auto text-[#52525B] mb-3" size={28} />
            <div className="font-semibold">{kind === "week" ? "No XP earned this week yet" : "No XP earned yet"}</div>
            <p className="text-sm text-[#A1A1AA] mt-1">Take a quiz to claim the top spot.</p>
          </div>
        )}
      </div>
    </div>
  );
}
